import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { PMField } from "../../utils/PMField";
import { useSelector } from "react-redux";
import { QuillEditor } from "../../utils/quill-editor";
import { purchasesApi } from "../../../api/inventory/purchases";
import { fetchContact } from "../../../api/customer/contact";
import { Button } from "@mui/material";

export const PurchaseSendDialog = (props) => {
  const { onClose, open, purchase, onSuccess } = props;
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);

  const initialValues = {
    email: purchase?.supplier?.email || "",
    name: purchase?.supplier?.name || "",
    message: "",
  };

  useEffect(() => {
    const loadContact = async () => {
      if (open && purchase?.contact?.id) {
        setIsLoadingContact(true);
        try {
          const response = await fetchContact(purchase.contact.id);
          if (response?.data) {
            setContact(response.data);
          }
        } catch (error) {
          console.error("Error fetching contact:", error);
        } finally {
          setIsLoadingContact(false);
        }
      } else {
        setContact(null);
      }
    };

    loadContact();
  }, [open, purchase?.contact?.id]);

  const handleReceiverChange = (receiver, formik) => {
    formik.setFieldValue("email", receiver?.email);
    formik.setFieldValue("name", receiver?.name);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="mt-3 sm:mt-5">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {t("Send innkjøp til leverandør")}
                  </Dialog.Title>
                  <p className="text-red-500 text-sm mt-2">
                    {t("Ved sending vil innkjøpet markeres som sendt")}
                  </p>

                  {!purchase?.id ? (
                    "Mangler ID"
                  ) : (
                    <Formik
                      initialValues={initialValues}
                      onSubmit={async (values, helpers) => {
                        try {
                          helpers.setSubmitting(true);

                          const response = await purchasesApi.sendToSupplier(purchase.id, {
                            email: values.email,
                            name: values.name,
                            message: values.message,
                          });

                          if (response?.data?.success) {
                            toast.success(t("Innkjøp sendt til leverandør!"));
                            helpers.setStatus({ success: true });
                            if (onSuccess) {
                              onSuccess();
                            }
                          } else {
                            throw new Error(
                              response?.data?.errors?.join(", ") ||
                                "Kunne ikke sende innkjøp"
                            );
                          }
                        } catch (err) {
                          toast.error(
                            t("Noe gikk galt. Kunne ikke sende innkjøp.")
                          );
                          console.error(err);
                          helpers.setStatus({ success: false });
                          helpers.setErrors({ submit: err.message });
                        } finally {
                          helpers.setSubmitting(false);
                        }
                      }}
                    >
                      {(formik) => (
                        <Form>
                          <div className="grid grid-cols-6 gap-6 mt-10">
                            <div className="col-span-6 sm:col-span-6">
                              <div className="flex flex-col">
                                <span className="text-gray-400 text-sm mb-2">
                                  {t("Send til")}{" "}
                                </span>
                                <span className="isolate inline-flex rounded-md shadow-sm">
                                  <button
                                    onClick={() =>
                                      handleReceiverChange(
                                        {
                                          email: currentUser?.email,
                                          name: currentUser?.name,
                                        },
                                        formik
                                      )
                                    }
                                    type="button"
                                    className="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                                  >
                                    {t("Meg")}
                                  </button>
                                  <button
                                    onClick={
                                      purchase?.supplier?.email
                                        ? () =>
                                            handleReceiverChange(
                                              {
                                                email: purchase?.supplier?.email,
                                                name: purchase?.supplier?.name,
                                              },
                                              formik
                                            )
                                        : () =>
                                            toast.error(
                                              "Leverandør e-post mangler"
                                            )
                                    }
                                    type="button"
                                    className="relative -ml-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                                  >
                                    {t("Leverandør")}
                                  </button>
                                  <button
                                    onClick={
                                      contact?.email
                                        ? () =>
                                            handleReceiverChange(
                                              {
                                                email: contact?.email,
                                                name: contact?.name || contact?.displayTitle,
                                              },
                                              formik
                                            )
                                        : () =>
                                            toast.error(
                                              t("Kontakt e-post mangler")
                                            )
                                    }
                                    type="button"
                                    disabled={isLoadingContact || !contact?.email}
                                    className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isLoadingContact ? t("Laster...") : t("Kontakt")}
                                  </button>
                                </span>
                              </div>
                            </div>
                            <div className="col-span-6 sm:col-span-6">
                              <PMField
                                name="name"
                                label={t("Navn")}
                                {...formik}
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-6">
                              <PMField
                                name="email"
                                label={t("E-post")}
                                {...formik}
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-6 mb-[200px]">
                              <span className="text-sm px-1">
                                Melding til leverandør
                              </span>
                              <div className="mt-1">
                                <QuillEditor
                                  name="message"
                                  rows={4}
                                  {...formik}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="mt-20 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <button
                              type="button"
                              className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                              onClick={onClose}
                            >
                              {t("Lukk")}{" "}
                            </button>

                            <button
                              type="button"
                              className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={formik.submitForm}
                              disabled={formik.isSubmitting}
                            >
                              {formik.isSubmitting
                                ? t("Sender...")
                                : t("Send")}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};





